import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Mail } from 'lucide-react';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  
  const isHomePage = location.pathname === '/';

  // Navigation items - some scroll on homepage, some navigate to pages
  const navItems = [
    { type: 'scroll', sectionId: 'hero', label: 'Startseite' },
    { type: 'scroll', sectionId: 'about', label: 'Über uns' },
    { type: 'link', path: '/leistungen', label: 'Leistungen' },
    { type: 'link', path: '/referenzen', label: 'Projekte' }
  ];

  const handleNavClick = (item) => {
    setIsMobileMenuOpen(false);
    
    if (item.type === 'scroll') {
      if (isHomePage) {
        // On homepage, scroll to section
        const element = document.getElementById(item.sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      } else {
        // On other pages, navigate to homepage and then scroll
        navigate('/');
        setTimeout(() => {
          const element = document.getElementById(item.sectionId);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
      }
    } else if (item.type === 'link') {
      navigate(item.path);
    }
  };

  const handleContactClick = () => {
    setIsMobileMenuOpen(false);
    
    if (isHomePage) {
      const element = document.getElementById('kontakt');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      navigate('/kontakt');
    }
  };

  const handleLogoClick = () => {
    if (isHomePage) {
      const element = document.getElementById('hero');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      navigate('/');
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white shadow-md">
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <div 
            className="flex items-center space-x-2 md:space-x-3 cursor-pointer group" 
            onClick={handleLogoClick}
            data-testid="header-logo"
          >
            <img
              src="/images/logo-hk-v2.png"
              alt="HK Altbausanierung Logo"
              className="h-14 md:h-20 w-auto transition-transform duration-300 group-hover:scale-105"
              width="80"
              height="80"
            />
            <div className="text-lg md:text-2xl font-bold tracking-tight">
              <span className="bg-gradient-to-r from-[#DC2626] to-[#B91C1C] bg-clip-text text-transparent">HK</span>
              <span className="text-gray-900 ml-1 md:ml-2">Altbausanierung</span>
            </div>
          </div>

          {/* Center Navigation */}
          <nav className="hidden lg:flex items-center space-x-1 absolute left-1/2 transform -translate-x-1/2" aria-label="Hauptnavigation">
            {navItems.map((item, index) => (
              <button
                key={index}
                onClick={() => handleNavClick(item)}
                className={`px-5 py-2 font-semibold transition-all duration-300 relative group rounded-lg hover:bg-gray-50 ${
                  (item.type === 'link' && location.pathname === item.path) 
                    ? 'text-[#DC2626]' 
                    : 'text-gray-700 hover:text-[#DC2626]'
                }`}
                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
              >
                {item.label}
                <span className={`absolute bottom-1 left-1/2 transform -translate-x-1/2 h-0.5 bg-[#DC2626] transition-all duration-300 rounded-full ${
                  (item.type === 'link' && location.pathname === item.path) 
                    ? 'w-3/4' 
                    : 'w-0 group-hover:w-3/4'
                }`}></span>
              </button>
            ))}
          </nav>

          {/* Contact Button */}
          <button
            onClick={handleContactClick}
            className="hidden lg:flex items-center space-x-2 bg-gradient-to-r from-[#DC2626] to-[#B91C1C] text-white px-6 py-3 rounded-full font-bold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
            data-testid="header-contact-btn"
          >
            <Mail className="w-4 h-4" />
            <span>Kontakt</span>
          </button>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden text-gray-700 hover:text-[#DC2626] transition-colors p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Menü öffnen"
            data-testid="mobile-menu-btn"
          >
            {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <nav className="lg:hidden mt-4 pb-4 border-t border-gray-100 pt-4 space-y-2" aria-label="Mobile Navigation">
            {navItems.map((item, index) => (
              <button
                key={index}
                onClick={() => handleNavClick(item)}
                className={`block w-full text-left py-2 px-4 rounded-lg transition-all duration-300 font-semibold ${
                  (item.type === 'link' && location.pathname === item.path)
                    ? 'text-[#DC2626] bg-red-50' 
                    : 'text-gray-700 hover:text-[#DC2626] hover:bg-gray-50'
                }`}
              >
                {item.label}
              </button>
            ))}
            <button
              onClick={handleContactClick}
              className="w-full flex items-center justify-center space-x-2 bg-gradient-to-r from-[#DC2626] to-[#B91C1C] text-white px-6 py-3 rounded-full font-bold shadow-lg mt-4"
            >
              <Mail className="w-4 h-4" />
              <span>Kontakt</span>
            </button>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
